import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useProduction,
  useShows,
  useDances,
  useCreateShow,
  useCreateDance,
} from '@/data/productions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/productions/$id')({
  component: ProductionDetail,
});

function ProductionDetail() {
  const { id } = Route.useParams();
  const production = useProduction(id);
  const shows = useShows(id);
  const dances = useDances(id);

  if (production.isLoading) return <p aria-live="polite">Loading…</p>;
  if (!production.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Production not found.
      </p>
    );
  }

  const p = production.data;

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/admin/productions"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Productions
        </Link>
        <h1 className="mt-2">{p.name}</h1>
        <p className="mt-1 text-[var(--color-ink-muted)]">
          {p.season}
          {p.venue_name ? ` · ${p.venue_name}` : ''}
        </p>
      </header>

      <ShowsSection productionId={id} shows={shows.data ?? []} />

      <DancesSection productionId={id} dances={dances.data ?? []} />

      <section aria-labelledby="ops-heading" className="grid gap-4 sm:grid-cols-2">
        <h2 id="ops-heading" className="sr-only">
          Production tools
        </h2>
        <div className="surface-elevated p-6">
          <h3 className="font-medium text-[var(--text-lg)]">Seat map</h3>
          <p className="mt-2 text-[var(--color-ink-muted)]">
            Configure the venue seating for ticket sales.
          </p>
          <div className="mt-4">
            <Link to="/admin/productions/$id/seat-map" params={{ id }}>
              <Button variant="secondary">Open seat map</Button>
            </Link>
          </div>
        </div>
        <div className="surface-elevated p-6">
          <h3 className="font-medium text-[var(--text-lg)]">Bulk import</h3>
          <p className="mt-2 text-[var(--color-ink-muted)]">
            Paste a CSV or pipe-delimited lineup to load multiple dances at once.
          </p>
          <div className="mt-4">
            <Link to="/admin/productions/$id/import-lineup" params={{ id }}>
              <Button variant="secondary">Import lineup</Button>
            </Link>
          </div>
        </div>
      </section>

      <section aria-labelledby="views-heading" className="grid gap-3 sm:grid-cols-3">
        <h2 id="views-heading" className="sr-only">
          Conflict + roster views
        </h2>
        <Link to="/admin/productions/$id/conflicts" params={{ id }}>
          <div className="surface-elevated h-full p-5 hover:bg-[var(--color-surface-sunken)]">
            <h3 className="font-medium">Conflicts</h3>
            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              Aggregated across all shows. Acknowledge accepted edge cases.
            </p>
          </div>
        </Link>
        <Link to="/admin/productions/$id/by-dancer" params={{ id }}>
          <div className="surface-elevated h-full p-5 hover:bg-[var(--color-surface-sunken)]">
            <h3 className="font-medium">By dancer</h3>
            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              Every dancer's appearances with quick-change gaps flagged.
            </p>
          </div>
        </Link>
        <Link to="/admin/productions/$id/by-family" params={{ id }}>
          <div className="surface-elevated h-full p-5 hover:bg-[var(--color-surface-sunken)]">
            <h3 className="font-medium">By family</h3>
            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              Siblings grouped with adjacency warnings.
            </p>
          </div>
        </Link>
      </section>
    </div>
  );
}

function ShowsSection({
  productionId,
  shows,
}: {
  productionId: string;
  shows: ReturnType<typeof useShows>['data'] extends infer T ? T : never;
}) {
  const createShow = useCreateShow();
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [performanceAt, setPerformanceAt] = useState('');
  // Sprint 11 — per-show ticket controls. Default to a 6-per-purchase cap
  // (typical recital ask) so a green-field admin doesn't accidentally let
  // one buyer hold the front row. Empty string = "unlimited".
  const [maxPerPurchase, setMaxPerPurchase] = useState<string>('6');
  const [salesOpen, setSalesOpen] = useState(true);
  const [salesOpenAt, setSalesOpenAt] = useState('');

  async function handleAdd() {
    try {
      const cap = maxPerPurchase.trim() === '' ? null : Number.parseInt(maxPerPurchase, 10);
      if (cap !== null && (!Number.isFinite(cap) || cap < 1 || cap > 200)) {
        toast({
          title: 'Bad cap',
          description: 'Tickets per purchase must be 1-200, or blank for unlimited.',
          variant: 'error',
        });
        return;
      }
      await createShow.mutateAsync({
        production_id: productionId,
        name: name.trim(),
        performance_at: new Date(performanceAt).toISOString(),
        intermission_minutes: 15,
        default_gap_seconds: 60,
        max_tickets_per_purchase: cap,
        ticket_sales_open: salesOpen,
        ticket_sales_open_at: salesOpenAt ? new Date(salesOpenAt).toISOString() : null,
      });
      setName('');
      setPerformanceAt('');
      setMaxPerPurchase('6');
      setSalesOpen(true);
      setSalesOpenAt('');
      setShowAdd(false);
      toast({ title: 'Show added', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't add show",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <section aria-labelledby="shows-heading">
      <div className="flex items-baseline justify-between">
        <h2 id="shows-heading" className="text-[var(--text-xl)]">
          Shows
        </h2>
        <Button variant="secondary" size="sm" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Cancel' : 'Add show'}
        </Button>
      </div>

      {showAdd && (
        <div className="surface-elevated mt-4 grid gap-4 p-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="show-name">Name</Label>
            <Input
              id="show-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Saturday matinee"
            />
          </div>
          <div>
            <Label htmlFor="show-at">When</Label>
            <Input
              id="show-at"
              type="datetime-local"
              value={performanceAt}
              onChange={(e) => setPerformanceAt(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="show-cap">Tickets per purchase</Label>
            <Input
              id="show-cap"
              type="number"
              min="1"
              max="200"
              value={maxPerPurchase}
              onChange={(e) => setMaxPerPurchase(e.target.value)}
              placeholder="blank = unlimited"
            />
            <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
              How many seats one parent can hold in a single Checkout. Blank = no cap.
            </p>
          </div>
          <div>
            <Label htmlFor="show-open">Sales status</Label>
            <select
              id="show-open"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={salesOpen ? 'open' : 'paused'}
              onChange={(e) => setSalesOpen(e.target.value === 'open')}
            >
              <option value="open">Open</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="show-open-at">Sales open at (optional)</Label>
            <Input
              id="show-open-at"
              type="datetime-local"
              value={salesOpenAt}
              onChange={(e) => setSalesOpenAt(e.target.value)}
            />
            <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
              Schedule a future on-sale time. Leave blank for "open now".
            </p>
          </div>
          <div className="sm:col-span-2">
            <Button
              onClick={() => void handleAdd()}
              disabled={!name.trim() || !performanceAt || createShow.isPending}
            >
              {createShow.isPending ? 'Adding…' : 'Save show'}
            </Button>
          </div>
        </div>
      )}

      {shows?.length === 0 && (
        <p className="mt-3 text-[var(--color-ink-muted)]">
          No shows yet. Add one to start building the lineup.
        </p>
      )}

      {shows && shows.length > 0 && (
        <ul className="mt-4 grid gap-2">
          {shows.map((s) => (
            <li key={s.id} className="surface flex items-center justify-between p-4">
              <div>
                <p className="font-medium">{s.name}</p>
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {new Date(s.performance_at).toLocaleString()}
                </p>
              </div>
              <Link
                to="/admin/productions/$id/shows/$showId"
                params={{ id: productionId, showId: s.id }}
                className="text-[var(--color-accent)] underline-offset-2 hover:underline"
              >
                Lineup →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DancesSection({
  productionId,
  dances,
}: {
  productionId: string;
  dances: ReturnType<typeof useDances>['data'] extends infer T ? T : never;
}) {
  const createDance = useCreateDance();
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [style, setStyle] = useState('jazz');
  const [length, setLength] = useState(180);

  async function handleAdd() {
    try {
      await createDance.mutateAsync({
        production_id: productionId,
        name: name.trim(),
        style: style.trim(),
        length_seconds: length,
      });
      setName('');
      toast({ title: 'Dance added', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't add dance",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <section aria-labelledby="dances-heading">
      <div className="flex items-baseline justify-between">
        <h2 id="dances-heading" className="text-[var(--text-xl)]">
          Dances
        </h2>
        <Button variant="secondary" size="sm" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Cancel' : 'Add dance'}
        </Button>
      </div>

      {showAdd && (
        <div className="surface-elevated mt-4 grid gap-4 p-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="dance-name">Name</Label>
            <Input id="dance-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="dance-style">Style</Label>
            <Input id="dance-style" value={style} onChange={(e) => setStyle(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="dance-len">Length (sec)</Label>
            <Input
              id="dance-len"
              type="number"
              min="15"
              max="1800"
              value={length}
              onChange={(e) => setLength(Number.parseInt(e.target.value, 10) || 180)}
            />
          </div>
          <div className="sm:col-span-3">
            <Button
              onClick={() => void handleAdd()}
              disabled={!name.trim() || createDance.isPending}
            >
              {createDance.isPending ? 'Adding…' : 'Save dance'}
            </Button>
          </div>
        </div>
      )}

      {dances?.length === 0 && <p className="mt-3 text-[var(--color-ink-muted)]">No dances yet.</p>}

      {dances && dances.length > 0 && (
        <ul className="mt-4 grid gap-2">
          {dances.map((d) => (
            <li key={d.id} className="surface flex items-center justify-between p-4">
              <div>
                <p className="font-medium">{d.name}</p>
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {d.style} · {Math.floor(d.length_seconds / 60)}:
                  {String(d.length_seconds % 60).padStart(2, '0')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone="neutral">{d.style}</Badge>
                <Link
                  to="/admin/dances/$danceId/media"
                  params={{ danceId: d.id }}
                  className="text-[var(--color-ink-muted)] text-[var(--text-sm)] underline hover:text-[var(--color-ink)]"
                >
                  Media
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
